import React, { useMemo, useState } from 'react';
import Header from '../../../Header';
import Footer from '../../../Footer';
import * as XLSX from 'xlsx';
import { adminFilesApi } from '../../../../api/adminFilesApi';

const DEFAULT_PREVIEW_ROWS = 5;
const MAX_PREVIEW_ROWS = 500;

const normalizeCell = (v) => {
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  return String(v).trim();
};

const buildColumnLabels = (colCount) => {
  const labels = [];
  for (let i = 0; i < colCount; i++) {
    let n = i;
    let s = '';
    do {
      s = String.fromCharCode(65 + (n % 26)) + s;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    labels.push(s);
  }
  return labels;
};

const AdminFilesPage = () => {
  const [isBarcodeUpdateOpen, setIsBarcodeUpdateOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState('');
  const [rows, setRows] = useState([]); // array of arrays
  const [maxCols, setMaxCols] = useState(0);
  const [skipRows, setSkipRows] = useState(1);
  const [previewRows, setPreviewRows] = useState(DEFAULT_PREVIEW_ROWS);
  const [articleColIdx, setArticleColIdx] = useState(0);
  const [barcodeColIdx, setBarcodeColIdx] = useState(1);
  const [parseError, setParseError] = useState('');
  const [submitState, setSubmitState] = useState({ loading: false, error: '', result: null });

  const clearAll = () => {
    setFileName('');
    setWorkbook(null);
    setSheetNames([]);
    setSheetName('');
    setRows([]);
    setMaxCols(0);
    setSkipRows(1);
    setPreviewRows(DEFAULT_PREVIEW_ROWS);
    setArticleColIdx(0);
    setBarcodeColIdx(1);
    setParseError('');
    setSubmitState({ loading: false, error: '', result: null });
  };

  const parseSheet = (wb, name) => {
    if (!wb || !name) return;
    const ws = wb.Sheets[name];
    if (!ws) {
      setParseError('Не вдалося знайти вибраний аркуш у файлі.');
      return;
    }
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: '' });
    const trimmed = (aoa || []).filter(r => Array.isArray(r) && r.some(c => String(c).trim() !== ''));
    if (!trimmed.length) {
      setRows([]);
      setMaxCols(0);
      setParseError('Аркуш порожній або не містить даних.');
      return;
    }
    setRows(trimmed);
    const computedMaxCols = Math.max(...trimmed.map(r => r.length));
    setMaxCols(computedMaxCols);
    // дефолт: 1ша колонка артикул, 2га штрихкод
    setArticleColIdx(0);
    setBarcodeColIdx(Math.min(1, Math.max(0, computedMaxCols - 1)));
  };

  const parseExcel = async (file) => {
    setParseError('');
    setSubmitState({ loading: false, error: '', result: null });
    if (!file) return;
    setFileName(file.name || '');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    setWorkbook(wb);
    const names = wb.SheetNames || [];
    setSheetNames(names);
    const firstSheetName = names.length ? names[0] : '';
    if (!firstSheetName) {
      setParseError('Не знайдено жодного аркуша в файлі.');
      return;
    }
    setSheetName(firstSheetName);
    parseSheet(wb, firstSheetName);
  };

  const headers = useMemo(() => {
    if (!rows.length || maxCols <= 0) return [];
    const labels = buildColumnLabels(maxCols);
    const skip = Math.max(0, Math.min(Number(skipRows) || 0, rows.length - 1));
    const sampleRow = rows[skip] || [];
    return labels.map((lbl, idx) => {
      const sample = normalizeCell(sampleRow[idx]);
      return sample ? `${lbl}: ${sample}` : lbl;
    });
  }, [rows, maxCols, skipRows]);

  const { mappings, duplicateArticles, preview, canSubmit, totalRows, dataRowsCount } = useMemo(() => {
    if (!rows.length || maxCols <= 0) {
      return { mappings: [], duplicateArticles: [], preview: [], canSubmit: false, totalRows: 0, dataRowsCount: 0 };
    }
    const aIdx = Number(articleColIdx);
    const bIdx = Number(barcodeColIdx);
    const skip = Math.max(0, Math.min(Number(skipRows) || 0, rows.length));
    const dataRows = rows.slice(skip);
    const map = new Map(); // article -> barcode (last wins)
    const dup = new Set();
    for (const r of dataRows) {
      const article = normalizeCell(r[aIdx]);
      const barcode = normalizeCell(r[bIdx]);
      if (!article) continue;
      if (map.has(article)) dup.add(article);
      map.set(article, barcode);
    }
    const list = Array.from(map.entries()).map(([productCode, barcode]) => ({ productCode, barcode: barcode || null }));
    const limit = Math.max(1, Math.min(Number(previewRows) || DEFAULT_PREVIEW_ROWS, MAX_PREVIEW_ROWS));
    const prev = list.slice(0, limit);
    return {
      mappings: list,
      duplicateArticles: Array.from(dup).slice(0, 20),
      preview: prev,
      canSubmit: list.length > 0,
      totalRows: rows.length,
      dataRowsCount: dataRows.length
    };
  }, [rows, maxCols, articleColIdx, barcodeColIdx, skipRows, previewRows]);

  const submit = async () => {
    setSubmitState({ loading: true, error: '', result: null });
    try {
      const res = await adminFilesApi.bulkUpdateBarcodesByProductCode(mappings);
      setSubmitState({ loading: false, error: '', result: res.data });
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.data || e?.message || 'Помилка оновлення.';
      setSubmitState({ loading: false, error: msg, result: null });
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col p-0">
          <Header />
        </div>
      </div>

      <div className="row">
        <div className="col-12 p-0">
          <div className="card">
            <div
              role="button"
              tabIndex={0}
              className="w-100 px-3 py-2"
              onClick={() => setIsBarcodeUpdateOpen(v => !v)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setIsBarcodeUpdateOpen(v => !v);
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              aria-expanded={isBarcodeUpdateOpen}
            >
              <div className="d-flex align-items-center">
                <span className="font-weight-bold mr-2" style={{ fontSize: '1.1rem', lineHeight: 1 }}>
                  {isBarcodeUpdateOpen ? '−' : '+'}
                </span>
                <span className="font-weight-bold">Оновлення штрихкоду по артикулу з Excel</span>
              </div>
            </div>

            {isBarcodeUpdateOpen ? (
              <div className="card-body pt-0">
                <div className="row">
                  <div className="col-12 col-lg-8">
              <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: '1rem' }}>
                <div>
                  <div className="font-weight-bold">Файл</div>
                  <div className="text-muted small">{fileName ? fileName : 'Файл не обрано'}</div>
                </div>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearAll} disabled={submitState.loading}>
                  Очистити
                </button>
              </div>

              <div className="form-group">
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx,.xls"
                  onChange={(e) => parseExcel(e.target.files && e.target.files[0])}
                  disabled={submitState.loading}
                />
              </div>

              {sheetName ? (
                <div className="alert alert-info py-2 mb-3">
                  <span className="font-weight-bold">Аркуш:</span>{' '}
                  <span className="font-weight-bold">{sheetName}</span>
                </div>
              ) : null}

              {sheetNames && sheetNames.length > 1 ? (
                <div className="form-group">
                  <label className="font-weight-bold">Обрати аркуш</label>
                  <select
                    className="form-control"
                    value={sheetName}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSheetName(next);
                      setParseError('');
                      setSubmitState({ loading: false, error: '', result: null });
                      parseSheet(workbook, next);
                    }}
                    disabled={submitState.loading}
                  >
                    {sheetNames.map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="row">
                <div className="col-12 col-md-6">
                  <label className="font-weight-bold">Скільки рядків пропустити з початку</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="form-control"
                    value={skipRows}
                    onChange={(e) => setSkipRows(e.target.value === '' ? 0 : Math.max(0, Number(e.target.value)))}
                    disabled={submitState.loading || !rows.length}
                  />
                </div>
                <div className="col-12 col-md-6 mt-3 mt-md-0">
                  <label className="font-weight-bold">Скільки рядків показувати в превʼю (макс {MAX_PREVIEW_ROWS})</label>
                  <input
                    type="number"
                    min={1}
                    max={MAX_PREVIEW_ROWS}
                    step={1}
                    className="form-control"
                    value={previewRows}
                    onChange={(e) => setPreviewRows(e.target.value === '' ? DEFAULT_PREVIEW_ROWS : Math.max(1, Math.min(MAX_PREVIEW_ROWS, Number(e.target.value))))}
                    disabled={submitState.loading}
                  />
                </div>
              </div>

              {parseError ? (
                <div className="alert alert-danger">{parseError}</div>
              ) : null}

              {rows.length ? (
                <>
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <label className="font-weight-bold">Колонка “Артикул”</label>
                      <select
                        className="form-control"
                        value={String(articleColIdx)}
                        onChange={(e) => setArticleColIdx(Number(e.target.value))}
                        disabled={submitState.loading}
                      >
                        {headers.map((h, idx) => <option key={`${idx}-${h}`} value={String(idx)}>{h}</option>)}
                      </select>
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="font-weight-bold">Колонка “Штрих код”</label>
                      <select
                        className="form-control"
                        value={String(barcodeColIdx)}
                        onChange={(e) => setBarcodeColIdx(Number(e.target.value))}
                        disabled={submitState.loading}
                      >
                        {headers.map((h, idx) => <option key={`${idx}-${h}`} value={String(idx)}>{h}</option>)}
                      </select>
                    </div>
                  </div>

                  <hr />

                  {duplicateArticles.length ? (
                    <div className="alert alert-warning mt-3 mb-0">
                      Є дублікати артикулів у файлі (показано до 20). Буде використано <strong>останнє</strong> значення штрихкоду.
                      <div className="small mt-1">{duplicateArticles.join(', ')}</div>
                    </div>
                  ) : null}

                  {submitState.error ? (
                    <div className="alert alert-danger mt-3 mb-0">{submitState.error}</div>
                  ) : null}

                  <div className="mt-3">
                    <div className="font-weight-bold mb-2">Превʼю</div>
                    <div className="table-responsive">
                      <table className="table table-sm table-striped">
                        <thead>
                          <tr>
                            <th style={{ width: '40%' }}>Артикул</th>
                            <th>Штрих код</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.map((it) => (
                            <tr key={it.productCode}>
                              <td className="text-monospace">{it.productCode}</td>
                              <td className="text-monospace">{it.barcode || ''}</td>
                            </tr>
                          ))}
                          {!preview.length ? (
                            <tr>
                              <td colSpan={2} className="text-muted">Немає даних для превʼю</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: '0.75rem' }}>
                    <div className="text-muted">
                      Рядків у файлі: <strong>{totalRows}</strong> • пропускаємо: <strong>{Math.max(0, Number(skipRows) || 0)}</strong> • рядків даних: <strong>{dataRowsCount}</strong> • унікальних артикулів: <strong>{mappings.length}</strong>
                    </div>
                    {submitState.result ? (
                      <div className="alert alert-success mt-3 mb-0">
                        Оновлено: <strong>{submitState.result.updated}</strong> • Запитів: <strong>{submitState.result.requested}</strong> • Унікальних: <strong>{submitState.result.uniqueRequested}</strong>
                        {submitState.result.notFoundProductCodes && submitState.result.notFoundProductCodes.length ? (
                          <div className="mt-2">
                            <div className="font-weight-bold">Не знайдено артикулів: {submitState.result.notFoundProductCodes.length}</div>
                            <div className="small text-monospace" style={{ maxHeight: '140px', overflow: 'auto' }}>
                              {submitState.result.notFoundProductCodes.join(', ')}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    <button type="button" className="btn btn-dark" onClick={submit} disabled={!canSubmit || submitState.loading}>
                      {submitState.loading ? 'Оновлюю…' : 'Підтвердити та оновити'}
                    </button>
                  </div>
                </>
              ) : null}
                  </div>

                  <div className="col-12 col-lg-4 mt-3 mt-lg-0">
                    <div className="card">
                      <div className="card-body">
                        <div className="font-weight-bold mb-2">Формат файлу</div>
                        <ul className="mb-0">
                          <li>Excel `.xlsx` або `.xls`</li>
                          <li>Дві колонки: артикул і штрихкод</li>
                          <li>Порожній штрихкод — встановить `null`</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col p-0">
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminFilesPage;

